import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Plus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import { graphql } from '@/gql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Switch } from '@/components/ui/Switch';
import { SortableList } from '@/components/ui/SortableList';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { eurosToCents, formatDateTime } from '@/lib/format';
import { useBackHref } from '@/hooks/useListLocation';
import { GuardSuggestion } from '@/components/GuardSuggestion';
import { graphqlErrorCode, graphqlErrorMessage, graphqlErrorReason } from '@/lib/graphqlError';
import { doorsReasonLabel } from '@/lib/labels';
import { useEmailGuard } from '@/lib/useEmailCheck';
import { Can } from '@/lib/permissions';
import { doorsWindowProblem } from './doorsWindow';
import { EventFormRow, type EventFormValues } from './EventFormRow';
import { useStageChannels } from './useStageChannels';
import { NightEntrancePanel } from './NightEntrancePanel';
import { NightSalesCard } from './NightSalesCard';

const NightDoc = graphql(/* GraphQL */ `
  query Night($id: UUID!) {
    night(id: $id) {
      id
      blueprintId
      blueprint {
        id
        title
      }
      startsAt
      title
      requireTicket
      ticketText
      dresscode
      minimumAge
      doorsOpen
      doorsClose
      published
      isActive
      externalId
      goingCount
      soldTickets
      guestTickets
      events {
        id
        title
        startsAt
        endsAt
        description
        posterUrl
        entrancePriceLabel
        offersTickets
        featured
      }
      stages {
        id
        name
        sort
        ticketPrice
        feePrice
        maxAmount
        startsAt
        endsAt
        entryStartsAt
        entryEndsAt
        entryWindowLabel
        lateFee
        websiteSale
        appSale
        posSale
        linkSale
        linkUrl
        memberSale
        door
        guestList
        memberMaxQuantity
        memberBuyerEmail
        soldCount
        isSoldOut
        isActive
      }
    }
  }
`);

const UpdateNightDoc = graphql(/* GraphQL */ `
  mutation UpdateNight($id: UUID!, $input: UpdateNightInput!) {
    updateNight(id: $id, input: $input) {
      id
      doorsOpen
      doorsClose
      isActive
    }
  }
`);

const PublishNightDoc = graphql(/* GraphQL */ `
  mutation PublishNight($id: UUID!) {
    publishNight(id: $id) {
      id
      published
    }
  }
`);

const UnpublishNightDoc = graphql(/* GraphQL */ `
  mutation UnpublishNight($id: UUID!) {
    unpublishNight(id: $id) {
      id
      published
    }
  }
`);

const CreateNightEventDoc = graphql(/* GraphQL */ `
  mutation CreateNightEvent($nightId: UUID!, $input: NightEventInput!) {
    createNightEvent(nightId: $nightId, input: $input) {
      id
    }
  }
`);

const UpdateNightEventDoc = graphql(/* GraphQL */ `
  mutation UpdateNightEvent($id: UUID!, $input: NightEventInput!) {
    updateNightEvent(id: $id, input: $input) {
      id
    }
  }
`);

const DeleteNightEventDoc = graphql(/* GraphQL */ `
  mutation DeleteNightEvent($id: UUID!) {
    deleteNightEvent(id: $id)
  }
`);

const CreateNightStageDoc = graphql(/* GraphQL */ `
  mutation CreateNightStage($nightId: UUID!, $input: NightStageInput!) {
    createNightStage(nightId: $nightId, input: $input) {
      id
    }
  }
`);

const UpdateNightStageDoc = graphql(/* GraphQL */ `
  mutation UpdateNightStage($id: UUID!, $input: NightStageInput!) {
    updateNightStage(id: $id, input: $input) {
      id
      isActive
    }
  }
`);

const ReorderNightStagesDoc = graphql(/* GraphQL */ `
  mutation ReorderNightStages($nightId: UUID!, $orderedIds: [UUID!]!) {
    reorderNightStages(nightId: $nightId, orderedIds: $orderedIds) {
      id
    }
  }
`);

const CreateMemberSaleDoc = graphql(/* GraphQL */ `
  mutation CreateMemberSale($input: CreateMemberSaleInput!) {
    createMemberSale(input: $input) {
      url
      buyerEmail
      maxQuantity
      invited
      stage {
        id
        name
      }
    }
  }
`);

const GuestListEntriesDoc = graphql(/* GraphQL */ `
  query GuestListEntries($nightId: UUID!) {
    guestListEntries(nightId: $nightId) {
      id
      entryId
      email
      claimed
      scanned
    }
  }
`);

const CreateGuestListEntryDoc = graphql(/* GraphQL */ `
  mutation CreateGuestListEntry($input: CreateGuestListEntryInput!) {
    createGuestListEntry(input: $input) {
      id
      email
      quantity
    }
  }
`);

const ResendGuestListInviteDoc = graphql(/* GraphQL */ `
  mutation ResendGuestListInvite($id: UUID!) {
    resendGuestListInvite(id: $id) {
      id
      email
      quantity
    }
  }
`);

function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** The event form's values as Go's NightEventInput (the times become instants); the form shows the thrown refusal for a start that does not convert. */
function nightEventInput(v: EventFormValues) {
  const startsAt = fromLocalInput(v.start);
  if (!startsAt) throw new Error('Title and Starts at are required.');
  return {
    title: v.title,
    startsAt,
    endsAt: fromLocalInput(v.end),
    description: v.description,
    posterPath: v.posterPath,
    entrancePriceLabel: v.entrancePriceLabel,
    offersTickets: v.offersTickets,
    featured: v.featured,
  };
}

/**
 * Go's entry-window rules arrive as BAD_USER_INPUT with a developer-flavoured
 * message; show them as plain sentences. Used on the stage row and on Save
 * night (a doors change re-checks every active stage).
 */
function entryWindowErrorMessage(err: unknown, fallback?: string): string {
  const msg = graphqlErrorMessage(err, fallback);
  if (graphqlErrorCode(err) !== 'BAD_USER_INPUT') return msg;
  if (/entry window must lie inside/i.test(msg)) {
    return "Entry must start and end inside the night's doors window.";
  }
  if (/entry window end must be after start/i.test(msg)) {
    return 'Entry ends must be after entry starts.';
  }
  if (/late fee must be >= 0/i.test(msg)) return 'Late fee cannot be negative.';
  if (/late fee is required/i.test(msg)) {
    return 'Late fee is required when entry ends before doors close (0 is allowed).';
  }
  return msg;
}

export function NightEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const backHref = useBackHref('/nights');
  const { data, previousData, loading, error, refetch } = useQuery(NightDoc, {
    variables: { id: id ?? '' },
    skip: !id,
    errorPolicy: 'all',
  });
  const night = (data ?? previousData)?.night;
  // The calendar writes a Night stub (title, start, published, active,
  // requireTicket, ticketText, dresscode). It cannot satisfy this page query:
  // it lacks minimum age, doors, events and stages, so the
  // form is seeded from the full result. The ticket text and the dress code
  // stay in the seed key as a guard: a first result without them would otherwise
  // lock the fields to empty.

  const [title, setTitle] = useState('');
  const [requireTicket, setRequireTicket] = useState(false);
  const [ticketText, setTicketText] = useState('');
  const [dresscode, setDresscode] = useState('');
  const [minimumAge, setMinimumAge] = useState('');
  const [doorsOpen, setDoorsOpen] = useState('');
  const [doorsClose, setDoorsClose] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const [memberOpen, setMemberOpen] = useState(false);
  const [memberUrl, setMemberUrl] = useState<string | null>(null);
  // Unsaved event forms (Add event), listed after the saved ones; the number is only a React key.
  const [drafts, setDrafts] = useState<number[]>([]);

  const seedKey = night
    ? `${night.id}:${night.ticketText ?? ''}:${night.dresscode ?? ''}`
    : null;
  if (night && seedKey !== seededFor) {
    setSeededFor(seedKey);
    setTitle(night.title);
    setRequireTicket(night.requireTicket);
    setTicketText(night.ticketText ?? '');
    setDresscode(night.dresscode ?? '');
    setMinimumAge(night.minimumAge != null ? String(night.minimumAge) : '');
    setDoorsOpen(toLocalInput(night.doorsOpen));
    setDoorsClose(toLocalInput(night.doorsClose));
    setIsActive(night.isActive);
  }

  const doorsProblem = doorsWindowProblem(doorsOpen, doorsClose);

  const [update, { loading: saving }] = useMutation(UpdateNightDoc);
  const [publish, { loading: publishing }] = useMutation(PublishNightDoc);
  const [unpublish, { loading: unpublishing }] = useMutation(UnpublishNightDoc);
  const [createNightEvent] = useMutation(CreateNightEventDoc);
  const [updateNightEvent] = useMutation(UpdateNightEventDoc);
  const [deleteNightEvent] = useMutation(DeleteNightEventDoc);
  const [createStage] = useMutation(CreateNightStageDoc);
  const [updateStage] = useMutation(UpdateNightStageDoc);
  const [reorderStages] = useMutation(ReorderNightStagesDoc);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || doorsProblem) return;
    setFormError(null);
    try {
      await update({
        variables: {
          id,
          input: {
            title: title.trim(),
            requireTicket,
            // Trimmed; an empty string clears it.
            ticketText: ticketText.trim(),
            dresscode: dresscode.trim(),
            minimumAge: minimumAge ? Number.parseInt(minimumAge, 10) : null,
            doorsOpen: fromLocalInput(doorsOpen),
            doorsClose: fromLocalInput(doorsClose),
            isActive,
          },
        },
      });
      await refetch();
    } catch (err) {
      const msg = entryWindowErrorMessage(err);
      const reason = doorsReasonLabel(graphqlErrorReason(err));
      if (reason) {
        setFormError(reason);
      } else if (/overlap/i.test(msg)) {
        setFormError(
          'Doors window overlaps another night in this venue. Scan fails closed if more than one night matches “now” — change the window so they do not overlap.',
        );
      } else {
        setFormError(msg);
      }
    }
  };

  if (loading && !night && !error) {
    return <p className="text-sm text-gray-500">Loading night…</p>;
  }
  if (error && !night) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Night
        </h1>
        <QueryErrorNotice error={error} />
      </div>
    );
  }
  if (!night || !id) {
    return <p className="text-sm text-gray-500">Night not found.</p>;
  }

  // The Guest list stage renders as a plain row, not a form; the forms are
  // numbered 0..n-1 in on-screen order for their E2E ids (see stageControlId).
  const stageForms = night.stages.filter((stage) => !stage.guestList);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {night.title}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Dated night
            {night.blueprint ? ` · blueprint ${night.blueprint.title}` : ' · not from a blueprint'}.
            Blueprint edits do not rewrite this night.
          </p>
          {night.externalId ? (
            <p
              id="admin.nights.externalId"
              className="mt-1 text-sm text-gray-500 dark:text-gray-400"
            >
              Imported from Stager · event {night.externalId}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            id="admin.nights.publishState"
            tone={night.published ? 'success' : 'neutral'}
          >
            {night.published ? 'Published' : 'Unpublished'}
          </Badge>
          <Badge tone={night.isActive ? 'success' : 'neutral'}>
            {night.isActive ? 'Active' : 'Inactive'}
          </Badge>
          <Button
            id="admin.nights.publish"
            type="button"
            variant={night.published ? 'outline' : 'primary'}
            size="sm"
            loading={publishing || unpublishing}
            onClick={async () => {
              setFormError(null);
              try {
                if (night.published) {
                  await unpublish({ variables: { id } });
                } else {
                  await publish({ variables: { id } });
                }
                await refetch();
              } catch (err) {
                setFormError(graphqlErrorMessage(err));
              }
            }}
          >
            {night.published ? 'Unpublish' : 'Publish'}
          </Button>
        </div>
      </div>

      <p id="admin.nights.counts" className="text-sm text-gray-500">
        {formatDateTime(night.startsAt)} · {night.soldTickets} sold · {night.guestTickets}{' '}
        {night.guestTickets === 1 ? 'guest' : 'guests'} · {night.goingCount} going
      </p>

      <FormLayout
        title="Night"
        onSubmit={handleSubmit}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-3">
            {formError ? <span className="max-w-md text-sm text-red-600">{formError}</span> : null}
            <Button variant="ghost" type="button" onClick={() => navigate(backHref)}>
              Back to calendar
            </Button>
            <Button
              type="submit"
              id="admin.nights.save"
              loading={saving}
              disabled={doorsProblem !== null}
            >
              Save night
            </Button>
          </div>
        }
      >
        <FormSection title="Night">
          <FormField label="Title" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </FormField>
          <Switch
            label="Require ticket"
            checked={requireTicket}
            onChange={(e) => setRequireTicket(e.target.checked)}
          />
          <FormField
            label="Ticket text"
            description="Shown to the holder of a claimed ticket on the ticket e-mail, PDF, ticket pages and app. Never on the website, never before the claim."
          >
            <textarea
              id="admin.nights.ticketText"
              value={ticketText}
              maxLength={1000}
              onChange={(e) => setTicketText(e.target.value)}
              rows={4}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            />
          </FormField>
          <FormField
            label="Dress code"
            description="Shown on the website's Good to know and in the app; empty shows 'None, come as you are'."
          >
            <Input
              id="admin.nights.dresscode"
              value={dresscode}
              maxLength={100}
              onChange={(e) => setDresscode(e.target.value)}
            />
          </FormField>
          <FormField label="Minimum age">
            <Input type="number" min={0} value={minimumAge} onChange={(e) => setMinimumAge(e.target.value)} />
          </FormField>
        </FormSection>
        <FormSection
          title="Visibility"
          description="Published and Active are independent. A sold-out past night can stay published while inactive."
        >
          <FormField
            label="Published"
            description="The public site and app may see and sell this night. Use Publish / Unpublish at the top of this page."
          >
            <Badge tone={night.published ? 'success' : 'neutral'}>
              {night.published ? 'Published' : 'Unpublished'}
            </Badge>
          </FormField>
          <FormField
            label="Active"
            description="This night still exists for admin and reporting. When off, it is hidden from pickers and no new tickets can be sold. Existing tickets and orders are left alone."
          >
            <Switch
              id="admin.nights.isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              label={isActive ? 'Active' : 'Inactive'}
            />
          </FormField>
        </FormSection>
        <FormSection
          title="Doors window"
          description="Required. Editable after snapshot. Windows in this venue must not overlap."
        >
          <FormField label="Doors open" required error={doorsOpen ? undefined : doorsProblem}>
            <Input
              type="datetime-local"
              value={doorsOpen}
              onChange={(e) => setDoorsOpen(e.target.value)}
              required
            />
          </FormField>
          <FormField label="Doors close" required error={doorsOpen ? doorsProblem : undefined}>
            <Input
              type="datetime-local"
              value={doorsClose}
              onChange={(e) => setDoorsClose(e.target.value)}
              required
            />
          </FormField>
        </FormSection>
      </FormLayout>

      <Card
        title="Events"
        subtitle="What the public sees: each event has its own title, time, description, poster and entrance price. Times must lie inside the doors window. At most one event offers tickets; a night needs at least one to be published. Featured events show in the homepage's Featured section (the first six upcoming featured events; when none is marked the homepage shows the first six upcoming events)."
        actions={
          <Button
            type="button"
            size="sm"
            id="admin.nights.events.add"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setDrafts((d) => [...d, (d[d.length - 1] ?? 0) + 1])}
          >
            Add event
          </Button>
        }
      >
        <div className="space-y-3">
          {night.events.map((event, i) => (
            <EventFormRow
              ticketsAllowed={requireTicket}
              ticketsNoun="night"
              key={event.id}
              idPrefix="admin.nights.event"
              index={i}
              timeInput="datetime-local"
              deleteNoun="event"
              withFeatured
              event={{
                ...event,
                start: toLocalInput(event.startsAt),
                end: toLocalInput(event.endsAt),
              }}
              defaultStart={toLocalInput(night.doorsOpen)}
              onSave={async (values) => {
                await updateNightEvent({ variables: { id: event.id, input: nightEventInput(values) } });
                await refetch();
              }}
              onDelete={async () => {
                await deleteNightEvent({ variables: { id: event.id } });
                await refetch();
              }}
            />
          ))}
          {drafts.map((key, j) => (
            <EventFormRow
              ticketsAllowed={requireTicket}
              ticketsNoun="night"
              key={`draft-${key}`}
              idPrefix="admin.nights.event"
              index={night.events.length + j}
              timeInput="datetime-local"
              deleteNoun="event"
              withFeatured
              defaultStart={toLocalInput(night.doorsOpen)}
              onSave={async (values) => {
                await createNightEvent({ variables: { nightId: id, input: nightEventInput(values) } });
                await refetch();
                setDrafts((d) => d.filter((k) => k !== key));
              }}
              onDelete={async () => setDrafts((d) => d.filter((k) => k !== key))}
            />
          ))}
          {night.events.length + drafts.length === 0 ? (
            <p className="text-sm text-gray-500">
              No events yet. Add one before publishing.
            </p>
          ) : null}
        </div>
      </Card>

      {requireTicket ? (
        <Card
          title="Stages"
          subtitle="Link is a stage sold only through its link and cannot be combined; Website requires App. Guests never see these flags. Member stages are created via Create member sale — not a free-form checkbox. The Guest list stage is opened by the Guest list section — not a checkbox and not Add stage. An inactive stage stops new sales on that phase; existing tickets are left alone."
          actions={
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                id="admin.nights.memberSale.open"
                onClick={() => setMemberOpen(true)}
              >
                Create member sale
              </Button>
              <Button
                type="button"
                size="sm"
                id="admin.nights.stages.add"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={async () => {
                  await createStage({
                    variables: {
                      nightId: id,
                      input: {
                        name: 'New stage',
                        ticketPriceCents: 0,
                        feePriceCents: 0,
                        websiteSale: true,
                        appSale: true,
                        posSale: false,
                        door: false,
                        linkSale: false,
                        isActive: true,
                        entryStartsAt: null,
                        entryEndsAt: null,
                        lateFeeCents: null,
                      },
                    },
                  });
                  await refetch();
                }}
              >
                Add stage
              </Button>
            </div>
          }
        >
          <SortableList
            items={night.stages}
            onReorder={async (orderedIds) => {
              await reorderStages({ variables: { nightId: id, orderedIds } });
              await refetch();
            }}
            renderItem={(stage, { dragHandle }) => (
              <NightStageRow
                key={stage.id}
                stage={stage}
                index={stageForms.findIndex((form) => form.id === stage.id)}
                doorsClose={night.doorsClose}
                onSave={async (input) => {
                  await updateStage({ variables: { id: stage.id, input } });
                  await refetch();
                }}
                dragHandle={dragHandle}
              />
            )}
          />
        </Card>
      ) : null}

      <Can permission="night:manage">
        <GuestListSection nightId={id} onChanged={() => refetch()} />
      </Can>

      <NightEntrancePanel nightId={id} />
      {night.requireTicket ? <NightSalesCard nightId={id} /> : null}

      <MemberSaleModal
        open={memberOpen}
        nightId={id}
        mintedUrl={memberUrl}
        onClose={() => {
          setMemberOpen(false);
          setMemberUrl(null);
        }}
        onMinted={(url) => {
          setMemberUrl(url);
          void refetch();
        }}
      />
    </div>
  );
}

type StageField =
  | 'name'
  | 'price'
  | 'fee'
  | 'maxSold'
  | 'saleStarts'
  | 'saleEnds'
  | 'entryStarts'
  | 'entryEnds'
  | 'lateFee'
  | 'entryLabel'
  | 'websiteSale'
  | 'appSale'
  | 'posSale'
  | 'door'
  | 'linkSale'
  | 'linkUrl'
  | 'linkUrlMissing'
  | 'copyLink'
  | 'active'
  | 'save';

/** E2E id of one control in the `index`-th stage form (0-based, on-screen order). */
const stageControlId = (index: number, field: StageField) =>
  `admin.nights.stage.${index}.${field}`;

function NightStageRow({
  stage,
  index,
  doorsClose,
  onSave,
  dragHandle,
}: {
  index: number;
  /** The night's saved doors close (instant); a stage entry window ending before it needs a late fee. */
  doorsClose?: string | null;
  stage: {
    id: string;
    name: string;
    ticketPrice: number;
    feePrice: number;
    maxAmount?: number | null;
    startsAt?: string | null;
    endsAt?: string | null;
    entryStartsAt?: string | null;
    entryEndsAt?: string | null;
    entryWindowLabel?: string | null;
    lateFee?: number | null;
    websiteSale: boolean;
    appSale: boolean;
    posSale: boolean;
    linkSale: boolean;
    linkUrl?: string | null;
    memberSale: boolean;
    door: boolean;
    guestList: boolean;
    memberMaxQuantity?: number | null;
    memberBuyerEmail?: string | null;
    soldCount: number;
    isActive: boolean;
  };
  onSave: (input: {
    name: string;
    ticketPriceCents: number;
    feePriceCents: number;
    maxAmount?: number | null;
    startsAt?: string | null;
    endsAt?: string | null;
    entryStartsAt?: string | null;
    entryEndsAt?: string | null;
    lateFeeCents?: number | null;
    websiteSale: boolean;
    appSale: boolean;
    posSale: boolean;
    door: boolean;
    linkSale: boolean;
    isActive: boolean;
  }) => Promise<void>;
  dragHandle: React.ReactNode;
}) {
  const [name, setName] = useState(stage.name);
  const [price, setPrice] = useState(String(stage.ticketPrice));
  const [fee, setFee] = useState(String(stage.feePrice));
  const [maxAmount, setMaxAmount] = useState(
    stage.maxAmount != null ? String(stage.maxAmount) : '',
  );
  const [saleStart, setSaleStart] = useState(toLocalInput(stage.startsAt));
  const [saleEnd, setSaleEnd] = useState(toLocalInput(stage.endsAt));
  const [entryStart, setEntryStart] = useState(toLocalInput(stage.entryStartsAt));
  const [entryEnd, setEntryEnd] = useState(toLocalInput(stage.entryEndsAt));
  const [lateFee, setLateFee] = useState(stage.lateFee != null ? String(stage.lateFee) : '');
  const {
    channels: { websiteSale, appSale, posSale, door, linkSale },
    setLink,
    setWebsite,
    setApp,
    setPos,
    setDoor,
  } = useStageChannels(stage);
  const [copied, setCopied] = useState(false);
  const [isActive, setIsActive] = useState(stage.isActive);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Go refuses a missing late fee (0 is allowed) when the entry window ends
  // before doors close; mirror that in the label so the admin knows up front.
  const entryEndMs = entryEnd ? new Date(entryEnd).getTime() : Number.NaN;
  const doorsCloseMs = doorsClose ? new Date(doorsClose).getTime() : Number.NaN;
  const lateFeeRequired = entryEndMs < doorsCloseMs;

  if (stage.guestList) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 p-3 dark:border-white/10">
        {dragHandle}
        <span className="flex-1 text-sm font-medium text-gray-900 dark:text-white">
          {stage.name}
        </span>
        <Badge tone="brand">Guest list</Badge>
        <span className="text-xs text-gray-500">
          {stage.soldCount} {stage.soldCount === 1 ? 'guest' : 'guests'}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 p-3 dark:border-white/10">
      <div className="flex items-center gap-2">
        {dragHandle}
        <Input
          id={stageControlId(index, 'name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="flex-1"
        />
        {stage.memberSale ? <Badge tone="info">Member</Badge> : null}
        {stage.entryWindowLabel ? (
          <Badge tone="info" id={stageControlId(index, 'entryLabel')}>
            {stage.entryWindowLabel}
          </Badge>
        ) : null}
        {!isActive ? <Badge tone="neutral">Inactive</Badge> : null}
        <span className="text-xs text-gray-500">{stage.soldCount} sold</span>
        <Switch
          id={stageControlId(index, 'active')}
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          label={isActive ? 'Active' : 'Inactive'}
        />
      </div>
      {stage.memberSale ? (
        <p className="text-xs text-gray-500">
          Buyer {stage.memberBuyerEmail ?? '—'} · max {stage.memberMaxQuantity ?? '—'}
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <FormField label="Admission (€)" htmlFor={stageControlId(index, 'price')}>
          <Input
            id={stageControlId(index, 'price')}
            type="number"
            step="0.01"
            min={0}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </FormField>
        <FormField label="Fee (€)" htmlFor={stageControlId(index, 'fee')}>
          <Input
            id={stageControlId(index, 'fee')}
            type="number"
            step="0.01"
            min={0}
            value={fee}
            onChange={(e) => setFee(e.target.value)}
          />
        </FormField>
        <FormField
          label="Late fee (€)"
          htmlFor={stageControlId(index, 'lateFee')}
          required={lateFeeRequired}
          description={
            lateFeeRequired ? 'Required: entry ends before doors close (0 is allowed).' : undefined
          }
        >
          <Input
            id={stageControlId(index, 'lateFee')}
            type="number"
            step="0.01"
            min={0}
            required={lateFeeRequired}
            aria-required={lateFeeRequired}
            value={lateFee}
            onChange={(e) => setLateFee(e.target.value)}
          />
        </FormField>
        <FormField label="Max sold" htmlFor={stageControlId(index, 'maxSold')}>
          <Input
            id={stageControlId(index, 'maxSold')}
            type="number"
            min={0}
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
          />
        </FormField>
        <FormField label="Sale starts" htmlFor={stageControlId(index, 'saleStarts')}>
          <Input
            id={stageControlId(index, 'saleStarts')}
            type="datetime-local"
            value={saleStart}
            onChange={(e) => setSaleStart(e.target.value)}
          />
        </FormField>
        <FormField
          label="Sale ends"
          htmlFor={stageControlId(index, 'saleEnds')}
          description="Empty = doors open for website, app and link; doors close for POS and door"
        >
          <Input
            id={stageControlId(index, 'saleEnds')}
            type="datetime-local"
            value={saleEnd}
            onChange={(e) => setSaleEnd(e.target.value)}
          />
        </FormField>
      </div>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-3">
        <FormField
          label="Entry starts"
          htmlFor={stageControlId(index, 'entryStarts')}
          description="Empty = doors open."
        >
          <Input
            id={stageControlId(index, 'entryStarts')}
            type="datetime-local"
            value={entryStart}
            onChange={(e) => setEntryStart(e.target.value)}
          />
        </FormField>
        <FormField
          label="Entry ends"
          htmlFor={stageControlId(index, 'entryEnds')}
          description="Empty = doors close."
        >
          <Input
            id={stageControlId(index, 'entryEnds')}
            type="datetime-local"
            value={entryEnd}
            onChange={(e) => setEntryEnd(e.target.value)}
          />
        </FormField>
      </div>
      <div className="flex flex-wrap gap-4">
        <Switch
          id={stageControlId(index, 'websiteSale')}
          label="Website"
          checked={websiteSale}
          onChange={(e) => setWebsite(e.target.checked)}
        />
        <Switch
          id={stageControlId(index, 'appSale')}
          label="App"
          checked={appSale}
          onChange={(e) => setApp(e.target.checked)}
        />
        <Switch
          id={stageControlId(index, 'posSale')}
          label="POS"
          checked={posSale}
          onChange={(e) => setPos(e.target.checked)}
        />
        <Switch
          id={stageControlId(index, 'door')}
          label="Door"
          checked={door}
          onChange={(e) => setDoor(e.target.checked)}
        />
        <Switch
          id={stageControlId(index, 'linkSale')}
          label="Link"
          tone="yellow"
          checked={linkSale}
          onChange={(e) => setLink(e.target.checked)}
        />
      </div>
      {stage.linkSale && !stage.linkUrl ? (
        <p id={stageControlId(index, 'linkUrlMissing')} className="text-sm text-gray-500 dark:text-gray-400">
          No event of this night offers tickets, so this link has no URL yet. It appears when an event offers tickets.
        </p>
      ) : null}
      {stage.linkSale && stage.linkUrl ? (
        <div className="flex items-center gap-2">
          <Input
            id={stageControlId(index, 'linkUrl')}
            className="min-w-0 flex-1"
            readOnly
            value={stage.linkUrl}
          />
          <Button
            id={stageControlId(index, 'copyLink')}
            type="button"
            size="sm"
            variant="outline"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(stage.linkUrl ?? '');
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              } catch {
                // clipboard is optional; the URL stays selectable in the field
              }
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      ) : null}
      <div className="flex justify-end">
        <Button
          id={stageControlId(index, 'save')}
          type="button"
          size="sm"
          loading={saving}
          onClick={async () => {
            setSaving(true);
            setSaveError(null);
            try {
              await onSave({
                name,
                ticketPriceCents: eurosToCents(Number.parseFloat(price) || 0),
                feePriceCents: eurosToCents(Number.parseFloat(fee) || 0),
                maxAmount: maxAmount ? Number.parseInt(maxAmount, 10) : null,
                startsAt: fromLocalInput(saleStart),
                endsAt: fromLocalInput(saleEnd),
                entryStartsAt: fromLocalInput(entryStart),
                entryEndsAt: fromLocalInput(entryEnd),
                lateFeeCents: lateFee.trim() ? eurosToCents(Number.parseFloat(lateFee) || 0) : null,
                websiteSale,
                appSale,
                posSale,
                door,
                linkSale,
                isActive,
              });
            } catch (err) {
              setSaveError(entryWindowErrorMessage(err, 'Could not save this stage.'));
            } finally {
              setSaving(false);
            }
          }}
        >
          Save stage
        </Button>
      </div>
      {saveError ? (
        <p className="text-right text-sm text-red-600 dark:text-red-400">{saveError}</p>
      ) : null}
    </div>
  );
}

function MemberSaleModal({
  open,
  nightId,
  mintedUrl,
  onClose,
  onMinted,
}: {
  open: boolean;
  nightId: string;
  mintedUrl: string | null;
  onClose: () => void;
  onMinted: (url: string) => void;
}) {
  const [create, { loading }] = useMutation(CreateMemberSaleDoc);
  const [name, setName] = useState('Member');
  const [price, setPrice] = useState('');
  const [fee, setFee] = useState('0');
  const [maxQuantity, setMaxQuantity] = useState('2');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const emailGuard = useEmailGuard();

  const mint = async (address: string) => {
    setError(null);
    if (!address || !price) {
      setError('Buyer email and price are required.');
      return;
    }
    setChecking(true);
    try {
      if (!(await emailGuard.screen(address))) return;
      const result = await create({
        variables: {
          input: {
            nightId,
            name: name.trim() || 'Member',
            ticketPriceCents: eurosToCents(Number.parseFloat(price) || 0),
            feePriceCents: eurosToCents(Number.parseFloat(fee) || 0),
            maxQuantity: Number.parseInt(maxQuantity, 10) || 1,
            buyerEmail: address,
          },
        },
      });
      const url = result.data?.createMemberSale.url;
      if (url) onMinted(url);
    } catch (err) {
      emailGuard.refused(err, address);
      setError(graphqlErrorMessage(err));
    } finally {
      setChecking(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create member sale"
      description="Mints a secret checkout URL and emails the buyer. The URL is returned once. This email is not marketing consent."
      footer={
        mintedUrl ? (
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              id="admin.nights.memberSale.submit"
              loading={loading || checking}
              onClick={() => void mint(buyerEmail.trim())}
            >
              Mint URL
            </Button>
          </div>
        )
      }
    >
      {mintedUrl ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            Secret checkout URL (copy now — it cannot be read back)
          </p>
          <Input readOnly id="admin.nights.memberSale.url" value={mintedUrl} />
        </div>
      ) : (
        <div className="space-y-3">
          <FormField label="Stage name">
            <Input id="admin.nights.memberSale.name" value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <FormField label="Admission (€)" required>
            <Input id="admin.nights.memberSale.price" type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
          </FormField>
          <FormField label="Booking fee (€)">
            <Input id="admin.nights.memberSale.fee" type="number" step="0.01" value={fee} onChange={(e) => setFee(e.target.value)} />
          </FormField>
          <FormField label="Max quantity" required>
            <Input id="admin.nights.memberSale.maxQuantity" type="number" min={1} value={maxQuantity} onChange={(e) => setMaxQuantity(e.target.value)} />
          </FormField>
          <FormField label="Buyer email" required>
            <Input
              id="admin.nights.memberSale.buyerEmail"
              type="email"
              value={buyerEmail}
              onChange={(e) => {
                setBuyerEmail(e.target.value);
                emailGuard.changed();
              }}
            />
          </FormField>
          <GuardSuggestion
            guard={emailGuard}
            idPrefix="admin.nights.memberSale"
            disabled={loading || checking}
            onAnswer={(address) => {
              setBuyerEmail(address);
              void mint(address);
            }}
          />
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </div>
      )}
    </Modal>
  );
}

type GuestListTicketRow = {
  id: string;
  entryId: string;
  email: string;
  claimed: boolean;
  scanned: boolean;
};

function groupGuestListTickets(tickets: readonly GuestListTicketRow[]) {
  const groups: { entryId: string; email: string; tickets: GuestListTicketRow[] }[] = [];
  const index = new Map<string, number>();
  for (const ticket of tickets) {
    const at = index.get(ticket.entryId);
    if (at == null) {
      index.set(ticket.entryId, groups.length);
      groups.push({ entryId: ticket.entryId, email: ticket.email, tickets: [ticket] });
    } else {
      groups[at].tickets.push(ticket);
    }
  }
  return groups;
}

function GuestListSection({
  nightId,
  onChanged,
}: {
  nightId: string;
  onChanged: () => Promise<unknown>;
}) {
  const { data, previousData, loading, error, refetch } = useQuery(GuestListEntriesDoc, {
    variables: { nightId },
    errorPolicy: 'all',
  });
  const [createEntry, { loading: creating }] = useMutation(CreateGuestListEntryDoc);
  const [resendInvite] = useMutation(ResendGuestListInviteDoc);
  const [email, setEmail] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [checking, setChecking] = useState(false);
  const emailGuard = useEmailGuard();
  const [formError, setFormError] = useState<string | null>(null);
  const [resendError, setResendError] = useState<{ entryId: string; message: string } | null>(
    null,
  );
  const [resendingId, setResendingId] = useState<string | null>(null);
  // The invite is e-mailed by Go; staff never see the link. Remember by entry id, for this session
  // only, which address got it.
  const [invitedEmails, setInvitedEmails] = useState<Record<string, string>>({});

  const view = data ?? previousData;
  const ticketRows = view?.guestListEntries;
  const groups = useMemo(() => groupGuestListTickets(ticketRows ?? []), [ticketRows]);

  const rememberInvited = (payload: { id: string; email: string }) => {
    setInvitedEmails((prev) => ({ ...prev, [payload.id]: payload.email }));
  };

  const submitEntry = async (address: string) => {
    setFormError(null);
    const qty = Number.parseInt(quantity, 10);
    if (!address || !Number.isInteger(qty) || qty < 1) {
      setFormError('Email and a quantity of at least 1 are required.');
      return;
    }
    setChecking(true);
    try {
      if (!(await emailGuard.screen(address))) return;
      const result = await createEntry({
        variables: {
          input: {
            nightId,
            email: address,
            quantity: qty,
          },
        },
      });
      const payload = result.data?.createGuestListEntry;
      if (!payload?.id) {
        setFormError('Could not create the guest-list entry.');
        return;
      }
      rememberInvited(payload);
      setEmail('');
      setQuantity('1');
    } catch (err) {
      emailGuard.refused(err, address);
      setFormError(graphqlErrorMessage(err));
      return;
    } finally {
      setChecking(false);
    }
    try {
      await refetch();
      await onChanged();
    } catch (err) {
      setFormError(
        graphqlErrorMessage(err, 'The entry was created, but the page could not be refreshed.'),
      );
    }
  };

  return (
    <Card
      id="admin.nights.guestList"
      title="Guest list"
      subtitle="Reserves tickets on the hidden Guest list stage. The invite is e-mailed to the address."
    >
      <div className="space-y-4">
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            void submitEntry(email.trim());
          }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <FormField
              className="min-w-0 flex-1"
              label="Email"
              htmlFor="admin.nights.guestList.email"
              required
            >
              <Input
                id="admin.nights.guestList.email"
                type="email"
                autoComplete="off"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  emailGuard.changed();
                }}
                required
              />
            </FormField>
            <FormField label="Quantity" htmlFor="admin.nights.guestList.quantity" required>
              <Input
                id="admin.nights.guestList.quantity"
                type="number"
                min={1}
                step={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                required
                className="sm:w-28"
              />
            </FormField>
            <Button
              id="admin.nights.guestList.submit"
              type="submit"
              loading={creating || checking}
            >
              Add to guest list
            </Button>
          </div>
          <GuardSuggestion
            guard={emailGuard}
            idPrefix="admin.nights.guestList"
            disabled={creating || checking}
            onAnswer={(address) => {
              setEmail(address);
              void submitEntry(address);
            }}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            This email is not marketing consent.
          </p>
          {formError ? (
            <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
          ) : null}
        </form>

        {error ? (
          <QueryErrorNotice error={error} title="Could not load the guest list" />
        ) : null}
        {loading && !view ? (
          <p className="text-sm text-gray-500">Loading guest list…</p>
        ) : null}
        {view && (ticketRows?.length ?? 0) === 0 ? (
          <p className="text-sm text-gray-500">There are no guest-list tickets.</p>
        ) : null}

        {groups.map((group) => {
          const invitedEmail = invitedEmails[group.entryId];
          return (
            <div
              key={group.entryId}
              className="space-y-3 rounded-xl border border-gray-200 p-3 dark:border-white/10"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {group.email}
                  <span className="ml-2 font-normal text-gray-500">
                    {group.tickets.length} {group.tickets.length === 1 ? 'ticket' : 'tickets'}
                  </span>
                </p>
                <Button
                  id={`admin.nights.guestList.resend.${group.entryId}`}
                  type="button"
                  size="sm"
                  variant="outline"
                  loading={resendingId === group.entryId}
                  onClick={async () => {
                    setResendError(null);
                    setResendingId(group.entryId);
                    try {
                      const result = await resendInvite({ variables: { id: group.entryId } });
                      const payload = result.data?.resendGuestListInvite;
                      if (!payload?.id) {
                        setResendError({
                          entryId: group.entryId,
                          message: 'Could not resend the invite.',
                        });
                        return;
                      }
                      rememberInvited(payload);
                    } catch (err) {
                      setResendError({
                        entryId: group.entryId,
                        message: graphqlErrorMessage(err),
                      });
                    } finally {
                      setResendingId(null);
                    }
                  }}
                >
                  Resend
                </Button>
              </div>
              {invitedEmail ? (
                <p
                  id={`admin.nights.guestList.invited.${group.entryId}`}
                  className="text-sm text-gray-700 dark:text-gray-300"
                >
                  Invite e-mailed to {invitedEmail}
                </p>
              ) : null}
              <ul className="space-y-1">
                {group.tickets.map((ticket) => (
                  <li
                    key={ticket.id}
                    className="flex flex-wrap items-baseline justify-between gap-2 text-sm text-gray-700 dark:text-gray-300"
                  >
                    <span className="font-mono text-xs text-gray-500">{ticket.id}</span>
                    <span>
                      {ticket.claimed ? 'Claimed' : 'Unclaimed'}
                      {' · '}
                      {ticket.scanned ? 'Scanned' : 'Not scanned'}
                    </span>
                  </li>
                ))}
              </ul>
              {resendError?.entryId === group.entryId ? (
                <p className="text-sm text-red-600 dark:text-red-400">{resendError.message}</p>
              ) : null}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
